/**
 * ContentHeaderSlot - Renders controls in the content-header location.
 *
 * This slot appears above the main content area and is typically used for
 * controls that need to be visible while browsing media.
 */

import React from 'react';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type TilePagination} from '../../tiles';
import {type StepMeta} from '../../types';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type SlotRegistry} from '../types';

export type ContentHeaderSlotProps = {
  controls: NonNullable<SlotRegistry[keyof SlotRegistry]>;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  mediaSliderKey: string;
  disableRunLinks?: boolean;
  isFullScreenPanelPage?: boolean;
  tilePagination?: TilePagination | null;
  panelViewerRef?: React.MutableRefObject<HTMLDivElement | null>;
};

/**
 * Renders controls in the content-header location.
 *
 * All controls (both label and interactive) are rendered through ControlRenderer,
 * which delegates to the appropriate control component that handles its own rendering.
 *
 * @param props - Content header slot props
 * @returns Rendered controls in header layout
 */
export const ContentHeaderSlot: React.FC<ContentHeaderSlotProps> = ({
  controls,
  config,
  updateConfig,
  stepMeta,
  disableRunLinks,
  isFullScreenPanelPage = false,
  tilePagination,
  panelViewerRef,
}) => {
  if (controls.length === 0) {
    return null;
  }

  return (
    <div className="w-full flex items-center justify-center gap-4 p-4">
      <div className="w-full flex flex-1 items-start justify-center shrink-0  " />
      {controls.map((control, idx) => (
        <ControlRenderer
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          key={`header-control-${idx}`}
          control={control}
          index={idx}
          config={config}
          updateConfig={updateConfig}
          stepMeta={stepMeta}
          disableRunLinks={disableRunLinks}
          columnIndex={undefined}
          isFullScreenPanelPage={isFullScreenPanelPage}
          tilePagination={tilePagination}
          panelViewerRef={panelViewerRef}
        />
      ))}
    </div>
  );
};
